/**
 * Embedding Model Registry
 *
 * Centralized configuration for all supported embedding models.
 * Models are loaded via transformers.js from HuggingFace Hub.
 *
 * To add a new model:
 * 1. Add entry to MODEL_REGISTRY with all required parameters
 * 2. Run `bun run models:download` to pre-download
 * 3. Test with `BK_MODEL=model-id bun run bench:search`
 */

export interface ModelConfig {
  /** HuggingFace model ID (e.g., "Xenova/bge-small-en-v1.5") */
  readonly id: string;
  /** Human-readable name */
  readonly name: string;
  /** Output embedding dimensions */
  readonly dimensions: number;
  /** Pooling strategy for sentence embeddings */
  readonly pooling: 'mean' | 'cls' | 'none';
  /** Whether to L2-normalize embeddings */
  readonly normalize: boolean;
  /** Prefix for query text (asymmetric search) */
  readonly queryPrefix: string;
  /** Prefix for document text (asymmetric search) */
  readonly docPrefix: string;
  /** Model category for grouping */
  readonly category: 'bge' | 'e5' | 'minilm' | 'gte' | 'nomic' | 'snowflake' | 'jina' | 'other';
  /** Approximate model size for reference */
  readonly sizeCategory: 'small' | 'base' | 'large';
  /** Notes about the model */
  readonly notes?: string;
}

/**
 * Registry of all supported embedding models.
 *
 * All models use Xenova/ prefix for transformers.js compatibility.
 * Models are downloaded to ~/.cache/huggingface-transformers/
 */
export const MODEL_REGISTRY: Record<string, ModelConfig> = {
  // ============================================================
  // BGE Models (BAAI) - Best for retrieval tasks
  // ============================================================
  'bge-small-en-v1.5': {
    id: 'Xenova/bge-small-en-v1.5',
    name: 'BGE Small English v1.5',
    dimensions: 384,
    pooling: 'cls',
    normalize: true,
    queryPrefix: 'Represent this sentence for searching relevant passages: ',
    docPrefix: '',
    category: 'bge',
    sizeCategory: 'small',
    notes: 'Default model. Best balance of speed and quality for code search.',
  },
  'bge-base-en-v1.5': {
    id: 'Xenova/bge-base-en-v1.5',
    name: 'BGE Base English v1.5',
    dimensions: 768,
    pooling: 'cls',
    normalize: true,
    queryPrefix: 'Represent this sentence for searching relevant passages: ',
    docPrefix: '',
    category: 'bge',
    sizeCategory: 'base',
    notes: '53% slower than small. Tested: regression on code search.',
  },
  'bge-large-en-v1.5': {
    id: 'Xenova/bge-large-en-v1.5',
    name: 'BGE Large English v1.5',
    dimensions: 1024,
    pooling: 'cls',
    normalize: true,
    queryPrefix: 'Represent this sentence for searching relevant passages: ',
    docPrefix: '',
    category: 'bge',
    sizeCategory: 'large',
    notes: 'Highest quality BGE but slow. Use for accuracy-critical tasks.',
  },
  'bge-small-en': {
    id: 'Xenova/bge-small-en',
    name: 'BGE Small English v1.0',
    dimensions: 384,
    pooling: 'cls',
    normalize: true,
    queryPrefix: 'Represent this sentence for searching relevant passages: ',
    docPrefix: '',
    category: 'bge',
    sizeCategory: 'small',
    notes: 'Older version. Use v1.5 instead.',
  },
  'bge-base-en': {
    id: 'Xenova/bge-base-en',
    name: 'BGE Base English v1.0',
    dimensions: 768,
    pooling: 'cls',
    normalize: true,
    queryPrefix: 'Represent this sentence for searching relevant passages: ',
    docPrefix: '',
    category: 'bge',
    sizeCategory: 'base',
    notes: 'Older version. Use v1.5 instead.',
  },

  // ============================================================
  // E5 Models (Microsoft) - Asymmetric retrieval
  // ============================================================
  'e5-small-v2': {
    id: 'Xenova/e5-small-v2',
    name: 'E5 Small v2',
    dimensions: 384,
    pooling: 'mean',
    normalize: true,
    queryPrefix: 'query: ',
    docPrefix: 'passage: ',
    category: 'e5',
    sizeCategory: 'small',
    notes: 'Tested: underperformed BGE on code search. Better for general text.',
  },
  'e5-base-v2': {
    id: 'Xenova/e5-base-v2',
    name: 'E5 Base v2',
    dimensions: 768,
    pooling: 'mean',
    normalize: true,
    queryPrefix: 'query: ',
    docPrefix: 'passage: ',
    category: 'e5',
    sizeCategory: 'base',
    notes: 'Larger E5 variant. Requires "query:" and "passage:" prefixes.',
  },
  'e5-large-v2': {
    id: 'Xenova/e5-large-v2',
    name: 'E5 Large v2',
    dimensions: 1024,
    pooling: 'mean',
    normalize: true,
    queryPrefix: 'query: ',
    docPrefix: 'passage: ',
    category: 'e5',
    sizeCategory: 'large',
    notes: 'Highest quality E5. Slow but accurate for general retrieval.',
  },
  'multilingual-e5-small': {
    id: 'Xenova/multilingual-e5-small',
    name: 'Multilingual E5 Small',
    dimensions: 384,
    pooling: 'mean',
    normalize: true,
    queryPrefix: 'query: ',
    docPrefix: 'passage: ',
    category: 'e5',
    sizeCategory: 'small',
    notes: 'Supports 100+ languages. Good for multilingual codebases.',
  },
  'multilingual-e5-base': {
    id: 'Xenova/multilingual-e5-base',
    name: 'Multilingual E5 Base',
    dimensions: 768,
    pooling: 'mean',
    normalize: true,
    queryPrefix: 'query: ',
    docPrefix: 'passage: ',
    category: 'e5',
    sizeCategory: 'base',
    notes: 'Supports 100+ languages. Larger multilingual variant.',
  },

  // ============================================================
  // MiniLM Models (Sentence Transformers) - Fast general-purpose
  // ============================================================
  'all-MiniLM-L6-v2': {
    id: 'Xenova/all-MiniLM-L6-v2',
    name: 'all-MiniLM-L6-v2',
    dimensions: 384,
    pooling: 'mean',
    normalize: true,
    queryPrefix: '',
    docPrefix: '',
    category: 'minilm',
    sizeCategory: 'small',
    notes: 'Popular universal model. No prefixes needed. Very fast.',
  },
  'all-MiniLM-L12-v2': {
    id: 'Xenova/all-MiniLM-L12-v2',
    name: 'all-MiniLM-L12-v2',
    dimensions: 384,
    pooling: 'mean',
    normalize: true,
    queryPrefix: '',
    docPrefix: '',
    category: 'minilm',
    sizeCategory: 'small',
    notes: 'Deeper MiniLM. Slightly better quality than L6.',
  },
  'paraphrase-MiniLM-L6-v2': {
    id: 'Xenova/paraphrase-MiniLM-L6-v2',
    name: 'paraphrase-MiniLM-L6-v2',
    dimensions: 384,
    pooling: 'mean',
    normalize: true,
    queryPrefix: '',
    docPrefix: '',
    category: 'minilm',
    sizeCategory: 'small',
    notes: 'Optimized for paraphrase detection. Good for similarity.',
  },
  'multi-qa-MiniLM-L6-cos-v1': {
    id: 'Xenova/multi-qa-MiniLM-L6-cos-v1',
    name: 'multi-qa-MiniLM-L6-cos-v1',
    dimensions: 384,
    pooling: 'mean',
    normalize: true,
    queryPrefix: '',
    docPrefix: '',
    category: 'minilm',
    sizeCategory: 'small',
    notes: 'Trained on 215M QA pairs. Good for question answering.',
  },

  // ============================================================
  // GTE Models (Alibaba) - State-of-the-art small models
  // ============================================================
  'gte-small': {
    id: 'Xenova/gte-small',
    name: 'GTE Small',
    dimensions: 384,
    pooling: 'mean',
    normalize: true,
    queryPrefix: '',
    docPrefix: '',
    category: 'gte',
    sizeCategory: 'small',
    notes: 'Competitive with larger models. No prefixes needed.',
  },
  'gte-base': {
    id: 'Xenova/gte-base',
    name: 'GTE Base',
    dimensions: 768,
    pooling: 'mean',
    normalize: true,
    queryPrefix: '',
    docPrefix: '',
    category: 'gte',
    sizeCategory: 'base',
    notes: 'Strong performance on MTEB benchmark.',
  },
  'gte-large': {
    id: 'Xenova/gte-large',
    name: 'GTE Large',
    dimensions: 1024,
    pooling: 'mean',
    normalize: true,
    queryPrefix: '',
    docPrefix: '',
    category: 'gte',
    sizeCategory: 'large',
    notes: 'Top MTEB scores. Slow but very accurate.',
  },

  // ============================================================
  // Nomic Models - Long context support
  // ============================================================
  'nomic-embed-text-v1': {
    id: 'nomic-ai/nomic-embed-text-v1',
    name: 'Nomic Embed Text v1',
    dimensions: 768,
    pooling: 'mean',
    normalize: true,
    queryPrefix: 'search_query: ',
    docPrefix: 'search_document: ',
    category: 'nomic',
    sizeCategory: 'base',
    notes: '8192 token context. May need trust_remote_code.',
  },
  'nomic-embed-text-v1.5': {
    id: 'nomic-ai/nomic-embed-text-v1.5',
    name: 'Nomic Embed Text v1.5',
    dimensions: 768,
    pooling: 'mean',
    normalize: true,
    queryPrefix: 'search_query: ',
    docPrefix: 'search_document: ',
    category: 'nomic',
    sizeCategory: 'base',
    notes: '8192 token context. Matryoshka embeddings support.',
  },

  // ============================================================
  // Other Notable Models
  // ============================================================
  'jina-embeddings-v2-small-en': {
    id: 'Xenova/jina-embeddings-v2-small-en',
    name: 'Jina Embeddings v2 Small',
    dimensions: 512,
    pooling: 'mean',
    normalize: true,
    queryPrefix: '',
    docPrefix: '',
    category: 'jina',
    sizeCategory: 'small',
    notes: '8192 token context. Good for long documents.',
  },
  'jina-embeddings-v2-base-en': {
    id: 'Xenova/jina-embeddings-v2-base-en',
    name: 'Jina Embeddings v2 Base',
    dimensions: 768,
    pooling: 'mean',
    normalize: true,
    queryPrefix: '',
    docPrefix: '',
    category: 'jina',
    sizeCategory: 'base',
    notes: '8192 token context. Larger Jina variant.',
  },
  'jina-embeddings-v2-base-code': {
    id: 'jinaai/jina-embeddings-v2-base-code',
    name: 'Jina Embeddings v2 Base Code',
    dimensions: 768,
    pooling: 'mean',
    normalize: true,
    queryPrefix: '',
    docPrefix: '',
    category: 'jina',
    sizeCategory: 'base',
    notes: '161M params. Code-specific (150M+ code QA pairs, 30+ langs). 8K context. May need trust_remote_code.',
  },

  // ============================================================
  // Snowflake Arctic Embed - Retrieval-optimized
  // ============================================================
  'snowflake-arctic-embed-xs': {
    id: 'Snowflake/snowflake-arctic-embed-xs',
    name: 'Snowflake Arctic Embed XS',
    dimensions: 384,
    pooling: 'cls',
    normalize: true,
    queryPrefix: 'Represent this sentence for searching relevant passages: ',
    docPrefix: '',
    category: 'snowflake',
    sizeCategory: 'small',
    notes: '22M params. Ultra-small retrieval model. Based on all-MiniLM-L6-v2.',
  },
  'snowflake-arctic-embed-s': {
    id: 'Snowflake/snowflake-arctic-embed-s',
    name: 'Snowflake Arctic Embed S',
    dimensions: 384,
    pooling: 'cls',
    normalize: true,
    queryPrefix: 'Represent this sentence for searching relevant passages: ',
    docPrefix: '',
    category: 'snowflake',
    sizeCategory: 'small',
    notes: '33M params. Same size as bge-small, trained for retrieval. Drop-in candidate.',
  },
  'snowflake-arctic-embed-m-v1.5': {
    id: 'Snowflake/snowflake-arctic-embed-m-v1.5',
    name: 'Snowflake Arctic Embed M v1.5',
    dimensions: 768,
    pooling: 'cls',
    normalize: true,
    queryPrefix: 'Represent this sentence for searching relevant passages: ',
    docPrefix: '',
    category: 'snowflake',
    sizeCategory: 'base',
    notes: '109M params. BEIR 55.14. Matryoshka (truncate to 256d). 7 ONNX quant variants.',
  },
  'snowflake-arctic-embed-m-v2.0': {
    id: 'Snowflake/snowflake-arctic-embed-m-v2.0',
    name: 'Snowflake Arctic Embed M v2.0',
    dimensions: 768,
    pooling: 'cls',
    normalize: true,
    queryPrefix: 'query: ',
    docPrefix: '',
    category: 'snowflake',
    sizeCategory: 'base',
    notes: '305M params. Multilingual, 8K context. Custom GTE arch — may need trust_remote_code.',
  },

  // ============================================================
  // ModernBERT Embedding Models - Latest architecture (2024+)
  // ============================================================
  'gte-modernbert-base': {
    id: 'Alibaba-NLP/gte-modernbert-base',
    name: 'GTE ModernBERT Base',
    dimensions: 768,
    pooling: 'cls',
    normalize: true,
    queryPrefix: '',
    docPrefix: '',
    category: 'gte',
    sizeCategory: 'base',
    notes: '149M params. CoIR code retrieval 79.31. BEIR 55.33. 8K context. No trust_remote_code. Top candidate.',
  },
  'modernbert-embed-base': {
    id: 'nomic-ai/modernbert-embed-base',
    name: 'ModernBERT Embed Base (Nomic)',
    dimensions: 768,
    pooling: 'mean',
    normalize: true,
    queryPrefix: 'search_query: ',
    docPrefix: 'search_document: ',
    category: 'nomic',
    sizeCategory: 'base',
    notes: '149M params. BEIR 52.89. Matryoshka (truncate to 256d). 8K context.',
  },
};

/**
 * Default model ID (short name, not full HuggingFace ID)
 */
export const DEFAULT_MODEL_ID = 'snowflake-arctic-embed-s';

/**
 * Get model config by short ID or full HuggingFace ID
 */
export function getModelConfig(modelId: string): ModelConfig | undefined {
  // Try direct lookup first
  if (modelId in MODEL_REGISTRY) {
    return MODEL_REGISTRY[modelId];
  }

  // Try matching by full HuggingFace ID
  for (const config of Object.values(MODEL_REGISTRY)) {
    if (config.id === modelId) {
      return config;
    }
  }

  return undefined;
}

/**
 * Get model config, throwing if not found
 */
export function getModelConfigOrThrow(modelId: string): ModelConfig {
  const config = getModelConfig(modelId);
  if (config === undefined) {
    const available = Object.keys(MODEL_REGISTRY).join(', ');
    throw new Error(`Unknown model: ${modelId}. Available: ${available}`);
  }
  return config;
}

/**
 * Get the configured model ID from environment or default
 *
 * Environment variables (in order of precedence):
 * - BK_MODEL: Short ID or full HuggingFace ID
 * - BK_EMBEDDING_MODEL: Alias for BK_MODEL
 */
export function getConfiguredModelId(): string {
  const envModel = process.env['BK_MODEL'] ?? process.env['BK_EMBEDDING_MODEL'];
  if (envModel !== undefined && envModel !== '') {
    // Validate it exists
    const config = getModelConfig(envModel);
    if (config === undefined) {
      console.warn(`Warning: Unknown model "${envModel}", using default "${DEFAULT_MODEL_ID}"`);
      return DEFAULT_MODEL_ID;
    }
    return envModel;
  }
  return DEFAULT_MODEL_ID;
}

/**
 * List all models, optionally filtered by category or size
 */
export function listModels(options?: {
  category?: ModelConfig['category'];
  sizeCategory?: ModelConfig['sizeCategory'];
}): ModelConfig[] {
  let models = Object.values(MODEL_REGISTRY);

  if (options?.category !== undefined) {
    models = models.filter((m) => m.category === options.category);
  }

  if (options?.sizeCategory !== undefined) {
    models = models.filter((m) => m.sizeCategory === options.sizeCategory);
  }

  return models;
}

/**
 * Get all model IDs for downloading
 */
export function getAllModelIds(): string[] {
  return Object.values(MODEL_REGISTRY).map((m) => m.id);
}

/**
 * Reranker model configurations
 */
export interface RerankerModelConfig {
  readonly id: string;
  readonly name: string;
  readonly notes?: string;
}

export const RERANKER_REGISTRY: Record<string, RerankerModelConfig> = {
  'ms-marco-MiniLM-L-6-v2': {
    id: 'Xenova/ms-marco-MiniLM-L-6-v2',
    name: 'MS MARCO MiniLM L6 v2',
    notes: 'Default reranker. Fast cross-encoder for passage ranking.',
  },
  'ms-marco-MiniLM-L-12-v2': {
    id: 'Xenova/ms-marco-MiniLM-L-12-v2',
    name: 'MS MARCO MiniLM L12 v2',
    notes: 'Deeper reranker. Better quality but slower.',
  },
  'bge-reranker-base': {
    id: 'Xenova/bge-reranker-base',
    name: 'BGE Reranker Base',
    notes: 'BGE-family cross-encoder. Better fit for BGE embeddings.',
  },
  'bge-reranker-large': {
    id: 'Xenova/bge-reranker-large',
    name: 'BGE Reranker Large',
    notes: 'Larger BGE cross-encoder. Higher quality, slower.',
  },
};

export const DEFAULT_RERANKER_ID = 'ms-marco-MiniLM-L-6-v2';
